import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  IDexcomIntegration,
  IDexcomSyncLog,
} from "../../../@types/admin/AdminDexcomIntegration";
import { TableSorted } from "../../../components/TableSorted";
import { useDexcomSyncLogs } from "../../../hooks/queries/admin/dexcomIntegration";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  ModalContainer,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
} from "../shared";
import { syncOutcomeLabel, syncOutcomeTone } from "./dexcomPresentation";

const DEFAULT_PAGE_SIZE = 25;

interface IDexcomSyncHistoryModalProps {
  integration: IDexcomIntegration;
}

/** One connection's sync attempts, newest first, paged on the server (EN-5232). */
export function DexcomSyncHistoryModal({
  integration,
}: IDexcomSyncHistoryModalProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const {
    logs,
    page: response,
    isLoading,
    isError,
  } = useDexcomSyncLogs(integration.integrationId, page, pageSize);

  const columns: ColumnDef<IDexcomSyncLog, string>[] = useMemo(
    () => [
      {
        accessorKey: "ranAt",
        header: "Ran at",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.ranAt, "-")}</span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "outcome",
        header: "Outcome",
        cell: ({ row }) => (
          <StatusBadge status={syncOutcomeTone(row.original.outcome)}>
            {syncOutcomeLabel(row.original.outcome)}
          </StatusBadge>
        ),
        enableSorting: false,
      },
      {
        id: "window",
        header: "Window requested",
        meta: { renderEmpty: true },
        cell: ({ row }) =>
          row.original.windowStart ? (
            <span>
              {getLongFormattedDate(row.original.windowStart, "-")} –{" "}
              {getLongFormattedDate(row.original.windowEnd, "-")}
            </span>
          ) : (
            <span>—</span>
          ),
        enableSorting: false,
      },
      {
        accessorKey: "readingsFetched",
        header: "Fetched",
        cell: ({ row }) => <span>{row.original.readingsFetched}</span>,
        enableSorting: false,
      },
      {
        accessorKey: "readingsStored",
        header: "Stored",
        cell: ({ row }) => <span>{row.original.readingsStored}</span>,
        enableSorting: false,
      },
      {
        accessorKey: "error",
        header: "Error",
        meta: { renderEmpty: true },
        cell: ({ row }) => <span>{row.original.error || "—"}</span>,
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <ModalContainer data-testid="dexcom-sync-history">
      <ModalHeader>
        <ModalTitle>
          Sync history ·{" "}
          {integration.patientName || `Patient ${integration.patientId}`}
        </ModalTitle>
        <ModalSubtitle>
          Every sync attempt from the last 30 days, newest first. The job runs
          every 5 minutes.
        </ModalSubtitle>
      </ModalHeader>

      {isError && <ErrorMessage>Failed to load the sync history.</ErrorMessage>}

      <TableContainer>
        <TableSorted
          columns={columns}
          data={logs}
          emptyStateText={
            isLoading ? <TableLoadingState /> : "No sync attempts recorded yet"
          }
        />
        <TablePagination
          pagination={{
            currentPage: response?.currentPage ?? page,
            pageSize: response?.pageSize ?? pageSize,
            totalPages: Math.max(1, response?.pages ?? 1),
            totalItems: response?.totalCount ?? logs.length,
            hasNextPage: response?.hasNextPage ?? false,
            hasPreviousPage: response?.hasPreviousPage ?? false,
          }}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </TableContainer>
    </ModalContainer>
  );
}
